import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col, Alert, Container, Spinner } from 'react-bootstrap';
import style from "../Activity.module.css";
import { FaTrash, FaCalendarPlus } from "react-icons/fa6";
import Swal from 'sweetalert2';
import { getActivityData, addDatesToActivity, deleteDateFromActivity, updateActivity } from "../../../utils/Data";

const EditActivity = ({ id, handleCloseModal }) => {
  const [activityData, setActivityData] = useState(null);
  const [nombre, setNombre] = useState("");
  const [isSingleClass, setIsSingleClass] = useState(false);
  const [fechas, setFechas] = useState([]);
  const [hora, setHora] = useState("");
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleGetActivityData = async () => {
      const activityData = await getActivityData(id);
      if (activityData.exists()) {
        const data = activityData.data();
        setActivityData(data);
        setNombre(data.nombre);
        setIsSingleClass(data.isSingleClass);
        setFechas(data.isSingleClass ? [data.fechas[0]] : data.fechas || []);
        setHora(data.hora || "");
      } else {
        console.log("No se encontró la actividad");
      }
    };

    handleGetActivityData();
  }, [id]);

  const handleNameChange = (e) => setNombre(e.target.value);;

  const handleHourChange = (e) => setHora(e.target.value);

  const handleCheckboxChange = () => {
    setIsSingleClass(!isSingleClass);
    if (isSingleClass) {
      setFechas([fechas[0]]);
    }
  };

  const handleDateChange = (e, index) => {
    const updatedDates = [...fechas];

    updatedDates[index] = {
      ...updatedDates[index],
      fecha: e.target.value,
    };

    setFechas(updatedDates);
  };

  const handleAddDate = async () => {
    const newDate = "";
    const usuariosInscritos = [];
    const profesoresInscritos = [];
    fechas.forEach((fecha) => {
      if (fecha.usuarios) {
        fecha.usuarios.forEach((usuario) => {
          if (!usuariosInscritos.some(u => u.id === usuario.id)) {
            usuariosInscritos.push({ ...usuario, presente: false, ausenteConAviso: false });
          }
        });
      }

      if (fecha.profesores) {
        fecha.profesores.forEach((profesor) => {
          if (!profesoresInscritos.some(p => p.id === profesor.id)) {
            profesoresInscritos.push({ ...profesor, presente: false, ausenteConAviso: false });
          }
        });
      }
    });

    const newDateWithUsers = {
      fecha: newDate,
      usuarios: usuariosInscritos,
      profesores: profesoresInscritos,
    };

    setFechas([...fechas, newDateWithUsers]);

    try {
      await addDatesToActivity(newDateWithUsers, id);
    } catch (error) {
      console.error("Error al agregar fecha:", error);
    }
  };

  const handleDeleteDate = async (date, index) => {
    try {
      Swal.fire({
        title: "¿Desea eliminar esta fecha?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Si, borrar",
        cancelButtonText: "Cancelar",
        customClass: {
          popup: 'swal-foreground'
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          await deleteDateFromActivity(date, id);
          const updatedDates = fechas.filter((_, i) => i !== index);
          setFechas(updatedDates);
          Swal.fire({
            title: "Fecha eliminada",
            icon: "success",
            customClass: {
              popup: 'swal-foreground'
            }
          });
        }
      });
    } catch (error) {
      console.error("Error al eliminar la fecha:", error);
    }
  };

  const handleSaveChanges = async () => {
    if (!nombre || (fechas.length === 0 || (isSingleClass && !fechas[0]))) {
      setError("Por favor, complete todos los campos.");
      return;
    }

    try {
      setLoading(true);
      await updateActivity(nombre, hora, isSingleClass, fechas, id);
      Swal.fire({
        title: "Actividad actualizada correctamente!",
        icon: "success",
      });
      handleCloseModal();
    } catch (error) {
      setLoading(false);
      console.error("Error al actualizar la actividad: ", error);
      setError("Hubo un error al guardar los cambios.");
    }
  };

  return (
    <Container className="py-2">
      {activityData ? (
        <Form>
          <Form.Group as={Row} controlId="nombre">
            <Form.Label className={style.formLabel}>Nombre de la actividad</Form.Label>
            <Col sm={10}>
              <Form.Control
                type="text"
                value={nombre}
                onChange={handleNameChange}
                required
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="hora" className="mt-3">
            <Form.Label className={style.formLabel}>Hora de la actividad</Form.Label>
            <Col sm={10}>
              <Form.Control
                type="time"
                value={hora}
                onChange={handleHourChange}
                required
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="isSingleClass" className="mt-3">
            <Form.Label className={style.formLabel}>Clase única</Form.Label>
            <Col sm={10}>
              <Form.Check
                type="switch"
                checked={isSingleClass}
                onChange={handleCheckboxChange}
              />
            </Col>
          </Form.Group>

          <h4 className={`${style.subtitle} mt-3`}>Fechas de la actividad:</h4>
          {isSingleClass ? (
            <Form.Group as={Row} controlId="fecha">
              <Col sm={10}>
                <Form.Control
                  type="date"
                  value={fechas[0] ? fechas[0].fecha : ""}
                  onChange={(e) => handleDateChange(e, 0)}    
                  required
                />
              </Col>
            </Form.Group>
          ) : (
            fechas.map((date, index) => (
              <Row key={index} className="mb-2">
                <Col sm={10}>
                  <Form.Control
                    type="date"
                    value={date.fecha}
                    onChange={(e) => handleDateChange(e, index)}
                    required
                  />
                </Col>
                <Col sm={2} className="align-content-center">
                  <Button
                    variant="danger"
                    type="button"
                    size="sm"
                    onClick={() => handleDeleteDate(date, index)}
                  >
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
            ))
          )}

          {!isSingleClass && (
            <Button
              variant="dark"
              type="button"
              onClick={handleAddDate}
              className={`${style.addDateBtn} secondaryBtn d-flex align-items-center`}
            >
              <FaCalendarPlus /> Agregar Fecha
            </Button>
          )}

          {error && <Alert variant="danger">{error}</Alert>}
          <br /><br />
          <Row>
            <Col className="d-flex justify-content-end">
              <Button variant="dark" onClick={handleCloseModal} className="secondaryBtn">
                Cancelar
              </Button>
              <Button variant="dark" onClick={handleSaveChanges} className="mx-4 primaryBtn d-flex align-items-center">
                Guardar Cambios {loading ? <Spinner animation="grow" size="sm" style={{ marginLeft: "6px" }} /> : null}
              </Button>
            </Col>
          </Row>
        </Form>
      ) : (
        <p>Cargando datos...</p>
      )}
    </Container>
  );
};

export default EditActivity;